import { useState } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PaymentForm } from "@/components/PaymentForm";
import { 
  CreditCard, 
  Calendar, 
  AlertTriangle, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  RefreshCw,
  DollarSign,
  TrendingUp,
  Bell,
  Play,
  Eye,
  Plus,
  Loader2,
  Wallet,
  Trash2,
  Star,
  Building2
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function Payments() {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  
  // Fetch data
  const { data: stats, isLoading: statsLoading } = trpc.paymentDashboard.getStats.useQuery();
  const { data: recentTransactions, isLoading: transactionsLoading } = trpc.paymentDashboard.getRecentTransactions.useQuery({});
  const { data: upcomingPayments, isLoading: upcomingLoading } = trpc.paymentDashboard.getUpcomingPayments.useQuery({ days: 14 });
  const { data: failedPayments, isLoading: failedLoading } = trpc.paymentDashboard.getFailedPayments.useQuery();
  const { data: activeAlerts, isLoading: alertsLoading } = trpc.paymentDashboard.getActiveAlerts.useQuery();
  const { data: retryLogs } = trpc.paymentDashboard.getRetryLogs.useQuery(
    { scheduledPaymentId: selectedPaymentId! },
    { enabled: !!selectedPaymentId }
  );
  
  // Payment methods
  const { data: paymentMethods, isLoading: methodsLoading } = trpc.paymentMethods.list.useQuery({});
  const setDefaultMethod = trpc.paymentMethods.setDefault.useMutation({
    onSuccess: () => utils.paymentMethods.list.invalidate(),
  });
  const deleteMethod = trpc.paymentMethods.delete.useMutation({
    onSuccess: () => utils.paymentMethods.list.invalidate(),
  });
  
  // Gateway status
  const credentialsQuery = trpc.payments.verifyCredentials.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
  });
  
  
  // Mutations
  const utils = trpc.useUtils();
  const processPending = trpc.paymentDashboard.processPendingPayments.useMutation({
    onSuccess: () => {
      utils.paymentDashboard.invalidate();
    },
  });
  const processRetries = trpc.paymentDashboard.processRetryPayments.useMutation({
    onSuccess: () => {
      utils.paymentDashboard.invalidate();
    },
  });
  const acknowledgeAlert = trpc.paymentDashboard.acknowledgeAlert.useMutation({
    onSuccess: () => {
      utils.paymentDashboard.getActiveAlerts.invalidate();
    },
  });
  const resolveAlert = trpc.paymentDashboard.resolveAlert.useMutation({
    onSuccess: () => {
      utils.paymentDashboard.getActiveAlerts.invalidate();
      setResolutionNotes("");
    },
  });
  const cancelPayment = trpc.paymentDashboard.cancelScheduledPayment.useMutation({
    onSuccess: () => {
      utils.paymentDashboard.invalidate();
    },
  });
  
  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };
  
  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };
  
  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive" | "outline"; icon: React.ReactNode }> = {
      approved: { variant: "default", icon: <CheckCircle2 className="w-3 h-3" /> },
      completed: { variant: "default", icon: <CheckCircle2 className="w-3 h-3" /> },
      pending: { variant: "secondary", icon: <Clock className="w-3 h-3" /> },
      processing: { variant: "secondary", icon: <RefreshCw className="w-3 h-3 animate-spin" /> },
      declined: { variant: "destructive", icon: <XCircle className="w-3 h-3" /> },
      failed: { variant: "destructive", icon: <XCircle className="w-3 h-3" /> },
      error: { variant: "destructive", icon: <AlertTriangle className="w-3 h-3" /> },
      voided: { variant: "outline", icon: <XCircle className="w-3 h-3" /> },
      refunded: { variant: "outline", icon: <RefreshCw className="w-3 h-3" /> },
      cancelled: { variant: "outline", icon: <XCircle className="w-3 h-3" /> },
    };
    
    const config = variants[status] || { variant: "outline" as const, icon: null };
    
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        {config.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const handlePaymentSuccess = () => {
    setIsPaymentDialogOpen(false);
    utils.paymentDashboard.invalidate();
  };
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Payment Management</h1>
            <p className="text-muted-foreground">
              Transaction history, scheduled payments, and alerts
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => processRetries.mutate()}
              disabled={processRetries.isPending}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${processRetries.isPending ? 'animate-spin' : ''}`} />
              Retry Failed
            </Button>
            <Button
              variant="outline"
              onClick={() => processPending.mutate()}
              disabled={processPending.isPending}
            >
              <Play className={`w-4 h-4 mr-2`} />
              Process Due
            </Button>
            <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
              <DialogTrigger asChild>
                <Button className="gap-2">
                  <Plus className="w-4 h-4" />
                  New Payment
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg p-0 overflow-hidden">
                <PaymentForm 
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setIsPaymentDialogOpen(false)}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
        
        {/* Gateway Status */}
        <Card>
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span className="font-medium">Gateway Status:</span>
                {credentialsQuery.isLoading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking...
                  </span>
                ) : credentialsQuery.data?.success ? (
                  <span className="flex items-center gap-2 text-green-500">
                    <CheckCircle2 className="w-4 h-4" />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-2 text-red-500">
                    <XCircle className="w-4 h-4" />
                    Disconnected
                  </span>
                )}
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => credentialsQuery.refetch()}
                disabled={credentialsQuery.isLoading}
              >
                <RefreshCw className={`w-4 h-4 ${credentialsQuery.isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardContent>
        </Card>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Collected</CardTitle>
              <DollarSign className="h-4 w-4 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-500">
                {statsLoading ? '...' : formatCurrency(stats?.transactionStats?.approvedAmount || 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats?.transactionStats?.totalApproved || 0} approved transactions
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <TrendingUp className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-500">
                {statsLoading ? '...' : `${(stats?.transactionStats?.successRate || 0).toFixed(1)}%`}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats?.transactionStats?.totalTransactions || 0} total transactions
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Scheduled This Week</CardTitle>
              <Calendar className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-amber-500">
                {statsLoading ? '...' : stats?.scheduledStats?.thisWeekDue || 0}
              </div>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(stats?.scheduledStats?.pendingAmount || 0)} pending
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Needs Attention</CardTitle>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500">
                {statsLoading ? '...' : (stats?.alertStats?.newAlerts || 0) + (stats?.scheduledStats?.totalFailed || 0)}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats?.alertStats?.newAlerts || 0} alerts, {stats?.scheduledStats?.totalFailed || 0} failed
              </p>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="failed">
              Failed
              {(stats?.scheduledStats?.totalFailed || 0) > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {stats?.scheduledStats?.totalFailed}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="alerts">
              Alerts
              {(stats?.alertStats?.newAlerts || 0) > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {stats?.alertStats?.newAlerts}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="payment-methods">
              <Wallet className="w-4 h-4 mr-1" />
              Saved Cards
            </TabsTrigger>

          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Recent Transactions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5" />
                    Recent Transactions
                  </CardTitle>
                  <CardDescription>Last 10 payment transactions</CardDescription>
                </CardHeader>
                <CardContent>
                  {transactionsLoading ? (
                    <div className="text-center py-4 text-muted-foreground">Loading...</div>
                  ) : !recentTransactions || recentTransactions.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">No transactions yet</div>
                  ) : (
                    <div className="space-y-3">
                      {recentTransactions.slice(0, 10).map((tx) => (
                        <div key={tx.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                          <div>
                            <p className="font-medium">{tx.customerFirstName} {tx.customerLastName}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(tx.createdAt)} • {tx.maskedAccount || 'Card'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{formatCurrency(tx.amount)}</p>
                            {getStatusBadge(tx.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Upcoming Payments */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5" />
                    Upcoming Payments
                  </CardTitle>
                  <CardDescription>Scheduled for the next 14 days</CardDescription>
                </CardHeader>
                <CardContent>
                  {upcomingLoading ? (
                    <div className="text-center py-4 text-muted-foreground">Loading...</div>
                  ) : !upcomingPayments || upcomingPayments.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">No upcoming payments</div>
                  ) : (
                    <div className="space-y-3">
                      {upcomingPayments.slice(0, 10).map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                          <div>
                            <p className="font-medium">{payment.customerFirstName} {payment.customerLastName}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(payment.scheduledDate)} • {payment.maskedAccount || 'Card on file'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">{formatCurrency(payment.amount)}</p>
                            {getStatusBadge(payment.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
                <CardDescription>All payment transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {transactionsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading transactions...</div>
                ) : !recentTransactions || recentTransactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">No transactions found</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Date</th>
                          <th className="text-left py-3 px-2">Customer</th>
                          <th className="text-left py-3 px-2">Amount</th>
                          <th className="text-left py-3 px-2">Method</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Auth Code</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentTransactions.map((tx) => (
                          <tr key={tx.id} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-2 text-sm">{formatDate(tx.createdAt)}</td>
                            <td className="py-3 px-2">
                              <div>
                                <p className="font-medium">{tx.customerFirstName} {tx.customerLastName}</p>
                                <p className="text-xs text-muted-foreground">{tx.maskedAccount}</p>
                              </div>
                            </td>
                            <td className="py-3 px-2 font-bold">{formatCurrency(tx.amount)}</td>
                            <td className="py-3 px-2">
                              <Badge variant="outline">{tx.paymentMethod === 'credit_card' ? 'Card' : 'ACH'}</Badge>
                            </td>
                            <td className="py-3 px-2">{getStatusBadge(tx.status)}</td>
                            <td className="py-3 px-2 text-sm font-mono">{tx.authCode || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Scheduled Tab */}
          <TabsContent value="scheduled">
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Payments</CardTitle>
                <CardDescription>Upcoming automatic payments (post-dated)</CardDescription>
              </CardHeader>
              <CardContent>
                {upcomingLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading scheduled payments...</div>
                ) : !upcomingPayments || upcomingPayments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No scheduled payments
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Scheduled Date</th>
                          <th className="text-left py-3 px-2">Customer</th>
                          <th className="text-left py-3 px-2">Amount</th>
                          <th className="text-left py-3 px-2">Card</th>
                          <th className="text-left py-3 px-2">Type</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {upcomingPayments.map((payment) => (
                          <tr key={payment.id} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-2 text-sm font-medium">{formatDate(payment.scheduledDate)}</td>
                            <td className="py-3 px-2">
                              <p className="font-medium">{payment.customerFirstName} {payment.customerLastName}</p>
                            </td>
                            <td className="py-3 px-2 font-bold">{formatCurrency(payment.amount)}</td>
                            <td className="py-3 px-2 text-sm">{payment.maskedAccount || 'On file'}</td>
                            <td className="py-3 px-2">
                              <Badge variant="outline">
                                {payment.isRecurring ? `${payment.paymentsCompleted || 0}/${payment.totalPaymentsScheduled}` : 'One-time'}
                              </Badge>
                            </td>
                            <td className="py-3 px-2">{getStatusBadge(payment.status)}</td>
                            <td className="py-3 px-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => cancelPayment.mutate({ scheduledPaymentId: payment.id })}
                                disabled={cancelPayment.isPending}
                              >
                                Cancel
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Failed Tab */}
          <TabsContent value="failed">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  Failed Payments
                </CardTitle>
                <CardDescription>Payments that failed and may need attention</CardDescription>
              </CardHeader>
              <CardContent>
                {failedLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading failed payments...</div>
                ) : !failedPayments || failedPayments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                    No failed payments
                  </div>
                ) : (
                  <div className="space-y-4">
                    {failedPayments.map((payment) => (
                      <div key={payment.id} className="p-4 rounded-lg border border-red-500/20 bg-red-500/5">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-bold">{payment.customerFirstName} {payment.customerLastName}</p>
                            <p className="text-sm text-muted-foreground">
                              Originally scheduled: {formatDate(payment.scheduledDate)}
                            </p>
                            <p className="text-sm text-red-500 mt-1">
                              {payment.failureReason || 'Unknown error'}
                            </p>
                            <div className="flex items-center gap-2 mt-2">
                              <Badge variant="outline">
                                Retries: {payment.retryCount || 0}/3
                              </Badge>
                              {payment.nextRetryAt && (
                                <Badge variant="secondary">
                                  Next retry: {formatDate(payment.nextRetryAt)}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold">{formatCurrency(payment.amount)}</p>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="mt-2"
                                  onClick={() => setSelectedPaymentId(payment.id)}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  View Logs
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Retry History</DialogTitle>
                                  <DialogDescription>
                                    Payment attempts for {payment.customerFirstName} {payment.customerLastName}
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {retryLogs?.map((log) => (
                                    <div key={log.id} className="p-3 rounded-lg bg-muted">
                                      <div className="flex items-center justify-between">
                                        <Badge variant={log.status === 'success' ? 'default' : 'destructive'}>
                                          Attempt {log.attemptNumber}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                          {formatDate(log.attemptedAt)}
                                        </span>
                                      </div>
                                      <p className="text-sm mt-2">
                                        {log.responseText || 'No response text'}
                                      </p>
                                      {log.responseCode && (
                                        <p className="text-xs text-muted-foreground mt-1">
                                          Code: {log.responseCode}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                  {(!retryLogs || retryLogs.length === 0) && (
                                    <p className="text-center text-muted-foreground py-4">
                                      No retry logs available
                                    </p>
                                  )}
                                </div>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-500" />
                  Payment Alerts
                </CardTitle>
                <CardDescription>Issues requiring your attention</CardDescription>
              </CardHeader>
              <CardContent>
                {alertsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading alerts...</div>
                ) : !activeAlerts || activeAlerts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500" />
                    No active alerts
                  </div>
                ) : (
                  <div className="space-y-4">
                    {activeAlerts.map((alert) => (
                      <div key={alert.id} className="p-4 rounded-lg border border-amber-500/20 bg-amber-500/5">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-5 h-5 text-amber-500" />
                              <Badge variant="outline">{alert.alertType.replace(/_/g, ' ')}</Badge>
                            </div>
                            <p className="mt-2">{alert.alertMessage}</p>
                            <p className="text-xs text-muted-foreground mt-2">
                              Created: {formatDate(alert.createdAt)}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            {alert.status === 'new' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => acknowledgeAlert.mutate({ alertId: alert.id })}
                                disabled={acknowledgeAlert.isPending}
                              >
                                Acknowledge
                              </Button>
                            )}
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="default" size="sm">
                                  Resolve
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Resolve Alert</DialogTitle>
                                  <DialogDescription>
                                    Add notes about how this issue was resolved
                                  </DialogDescription>
                                </DialogHeader>
                                <Textarea
                                  placeholder="Resolution notes..."
                                  value={resolutionNotes}
                                  onChange={(e) => setResolutionNotes(e.target.value)}
                                />
                                <Button
                                  onClick={() => resolveAlert.mutate({
                                    alertId: alert.id,
                                    resolutionNotes,
                                  })}
                                  disabled={resolveAlert.isPending}
                                >
                                  Mark as Resolved
                                </Button>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Payment Methods Tab */}
          <TabsContent value="payment-methods">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Saved Payment Methods
                </CardTitle>
                <CardDescription>
                  Cards and bank accounts on file for scheduled payments
                </CardDescription>
              </CardHeader>
              <CardContent>
                {methodsLoading ? (
                  <div className="text-center py-8 text-muted-foreground">Loading payment methods...</div>
                ) : !paymentMethods || paymentMethods.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Wallet className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No saved payment methods
                    <p className="text-sm mt-2">Payment methods are saved when processing payments with "Save card on file" enabled</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-3 px-2">Account</th>
                          <th className="text-left py-3 px-2">Card/Account</th>
                          <th className="text-left py-3 px-2">Type</th>
                          <th className="text-left py-3 px-2">Holder</th>
                          <th className="text-left py-3 px-2">Expires</th>
                          <th className="text-left py-3 px-2">Status</th>
                          <th className="text-left py-3 px-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paymentMethods.map((method) => (
                          <tr key={method.id} className="border-b hover:bg-muted/50">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">#{method.accountId}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <CreditCard className="w-4 h-4" />
                                <span className="font-mono">{method.maskedAccount}</span>
                                {method.nickname && (
                                  <Badge variant="outline" className="text-xs">{method.nickname}</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <Badge variant="secondary">
                                {method.cardType || (method.paymentType === 'ach' ? 'ACH' : 'Card')}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-sm">{method.holderName}</td>
                            <td className="py-3 px-2 text-sm">
                              {method.expMonth && method.expYear ? (
                                <span className={`font-mono ${
                                  new Date(`20${method.expYear}-${method.expMonth}-01`) < new Date() 
                                    ? 'text-red-500' 
                                    : ''
                                }`}>
                                  {method.expMonth}/{method.expYear}
                                </span>
                              ) : '-'}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                {method.isDefault ? (
                                  <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">
                                    <Star className="w-3 h-3 mr-1" />
                                    Default
                                  </Badge>
                                ) : (
                                  <Badge variant="outline">Active</Badge>
                                )}
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-1">
                                {!method.isDefault && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setDefaultMethod.mutate({ id: method.id, accountId: method.accountId })}
                                    disabled={setDefaultMethod.isPending}
                                    title="Set as default"
                                  >
                                    <Star className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => deleteMethod.mutate({ id: method.id })}
                                  disabled={deleteMethod.isPending}
                                  className="text-red-500 hover:text-red-600"
                                  title="Remove"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          

        </Tabs>
      </div>
    </DashboardLayout>
  );
}
